import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import utils from '../utils'
import HouseCard from '../components/houses/HouseCard'
import { useApp } from '../context/AppContext'
import authServices from '../services/AuthServices'
import Loading from '../components/feedback/Loading'
import Empty from '../components/feedback/Empty'

function HousesPage() {
    const { categories } = useApp()
    const [properties, setProperties] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedCategory, setSelectedCategory] = useState('')
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [hasPrev, setHasPrev] = useState(false)

    useEffect(() => {
        setLoading(true)
        const params = selectedCategory 
            ? `?category=${selectedCategory}&page=${page}` 
            : `?page=${page}`

        authServices.getProperties(params)
            .then(res => {
                const data = res.data
                if (data.results) {
                    setProperties(data.results)
                    setHasNext(!!data.next)
                    setHasPrev(!!data.previous)
                } else {
                    setProperties(Array.isArray(data) ? data : [])
                    setHasNext(false)
                    setHasPrev(false)
                }
            })
            .catch(err => console.error('Failed to load houses:', err))
            .finally(() => setLoading(false))
    }, [selectedCategory, page])

    const handleFilterChange = (catId) => {
        setSelectedCategory(catId)
        setPage(1)
    }

    return (
        <div>
            <Helmet>
                <title>Houses - {utils.siteName}</title>
            </Helmet>

            <div className="bg-primary/5">
                <header className="px-8 mb-16 max-w-7xl mx-auto py-3">
                    <span className="block text-xs uppercase tracking-widest font-label text-secondary mb-4">Our Residenses</span>
                    <h1 className="text-3xl md:text-5xl font-headline text-primary max-w-3xl leading-tight">Curated Sanctuaries</h1>
                    <div className="w-24 h-1 bg-secondary mt-5"></div>
                </header>
            </div>

            <section className="mb-16 flex flex-wrap justify-center gap-3 px-4">
                <button
                    onClick={() => handleFilterChange('')}
                    className={`px-6 py-2 rounded-full text-xs font-label uppercase tracking-widest transition-all shadow-sm ${
                        selectedCategory === ''
                            ? 'bg-primary text-on-primary'
                            : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                    }`}
                >
                    All
                </button>

                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => handleFilterChange(String(cat.id))}
                        className={`px-6 py-2 rounded-full text-xs font-label uppercase tracking-widest transition-all shadow-sm ${
                            selectedCategory === String(cat.id)
                                ? 'bg-primary text-on-primary'
                                : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                        }`}
                    >
                        {cat.name}
                    </button>
                ))}
            </section>

            <div className="max-w-7xl mx-auto mb-16 px-8">
                {loading ? (
                    <div className="py-20 flex justify-center">
                        <Loading />
                    </div>
                ) : properties.length === 0 ? (
                    <div className="py-10">
                        <Empty objectName="houses in this category" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16">
                        {properties.map(property => (
                            <HouseCard key={property.id} property={property} />
                        ))}
                    </div>
                )}
            </div>

            {properties.length > 0 && (
                <div className="flex items-center justify-center gap-4 mt-16 mb-16">
                    <button
                        onClick={() => setPage(p => p - 1)}
                        disabled={!hasPrev || loading}
                        className="ghost-border px-6 py-3 rounded-xl text-primary font-bold text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <FaChevronLeft className="inline mr-2" /> Prev
                    </button>
                    <span className="text-xs text-on-surface-variant/70 font-label uppercase tracking-widest">
                        Page {page}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={!hasNext || loading}
                        className="ghost-border px-6 py-3 rounded-xl text-primary font-bold text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <FaChevronRight className="inline ml-2" />
                    </button>
                </div>
            )}
        </div>
    )
}

export default HousesPage